import { StatCard } from '@/components/ganado/StatCard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCattle } from '@/contexts/CattleContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Beef, TrendingUp, Package, Calendar, Plus, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function GanadoDashboard() {
  const { cattle, weightRecords, getLatestWeight } = useCattle();

  // Calculate statistics
  const activeCattle = cattle.filter(c => c.status === 'Activo').length;
  const totalCattle = cattle.length;
  
  // Calculate average weight gain
  const calculateAverageGain = () => {
    let totalGain = 0;
    let count = 0;
    
    cattle.forEach(c => {
      const weights = weightRecords
        .filter(w => w.cattleId === c.id)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      
      if (weights.length >= 2) {
        const firstWeight = weights[0].weight;
        const lastWeight = weights[weights.length - 1].weight;
        const days = Math.abs(new Date(weights[weights.length - 1].date) - new Date(weights[0].date)) / (1000 * 60 * 60 * 24);
        if (days > 0) {
          const dailyGain = (lastWeight - firstWeight) / days;
          totalGain += dailyGain;
          count++;
        }
      }
    });
    
    return count > 0 ? (totalGain / count).toFixed(2) : '0.00';
  };

  // Get recent weight records for chart
  const getChartData = () => {
    const data = {};
    
    weightRecords.forEach(record => {
      const cattleData = cattle.find(c => c.id === record.cattleId);
      if (cattleData) {
        if (!data[record.date]) {
          data[record.date] = { date: record.date };
        }
        data[record.date][cattleData.tag] = record.weight;
      }
    });
    
    return Object.values(data)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(-10); // Last 10 records
  };

  const chartData = getChartData();
  const avgDailyGain = calculateAverageGain();

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-accent/5 to-secondary/10 border border-primary/20 p-8 md:p-12">
        <div className="relative z-10 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Bienvenido a Ganadería
          </h1>
          <p className="text-lg text-muted-foreground mb-6">
            Sistema inteligente de gestión y seguimiento de ganado de engorde
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/ganaderia/registro">
              <Button size="lg" className="shadow-lg hover:shadow-xl transition-all">
                <Plus className="mr-2 h-5 w-5" />
                Registrar Ganado
              </Button>
            </Link>
            <Link to="/ganaderia/peso">
              <Button size="lg" variant="outline" className="shadow-sm hover:shadow-md transition-all">
                <TrendingUp className="mr-2 h-5 w-5" />
                Registrar Peso
              </Button>
            </Link>
          </div>
        </div>
        {/* Decorative element */}
        <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute -right-10 -top-10 w-48 h-48 bg-accent/10 rounded-full blur-2xl" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total de Ganado"
          value={totalCattle}
          subtitle="Registros totales"
          icon={Beef}
          color="primary"
          trend="up"
          trendValue="+3 este mes"
        />
        <StatCard
          title="Ganado Activo"
          value={activeCattle}
          subtitle="En engorde"
          icon={Package}
          color="success"
        />
        <StatCard
          title="Ganancia Promedio"
          value={`${avgDailyGain} kg`}
          subtitle="Por día"
          icon={TrendingUp}
          color="accent"
          trend="up"
          trendValue="Óptimo"
        />
        <StatCard
          title="Registros de Peso"
          value={weightRecords.length}
          subtitle="Total de mediciones"
          icon={Calendar}
          color="secondary"
        />
      </div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Weight Trend Chart */}
        <Card className="lg:col-span-2 border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Tendencia de Peso
            </CardTitle>
            <CardDescription>Evolución del peso en los últimos registros</CardDescription>
          </CardHeader>
          <CardContent>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="date" 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  {cattle.slice(0, 3).map((c, index) => (
                    <Line 
                      key={c.id}
                      type="monotone" 
                      dataKey={c.tag} 
                      stroke={`hsl(var(--chart-${index + 1}))`}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                <p>No hay datos de peso disponibles</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Cattle */}
        <Card className="border-border/50 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Beef className="h-5 w-5 text-primary" />
              Ganado Reciente
            </CardTitle>
            <CardDescription>Registros más recientes</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {cattle.slice(0, 5).map((c) => {
                const latestWeight = getLatestWeight(c.id);
                return (
                  <Link key={c.id} to={`/ganaderia/detalle/${c.id}`}>
                    <div className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer group">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center font-semibold text-sm">
                          {c.tag.split('-')[1]}
                        </div>
                        <div>
                          <p className="font-medium text-sm group-hover:text-primary transition-colors">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.breed}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        {latestWeight && (
                          <p className="text-sm font-semibold text-primary">{latestWeight.weight} kg</p>
                        )}
                        <Badge variant="outline" className="text-xs mt-1">
                          {c.status}
                        </Badge>
                      </div>
                    </div>
                  </Link>
                );
              })}
              {cattle.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Beef className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No hay ganado registrado</p>
                  <Link to="/ganaderia/registro">
                    <Button variant="link" className="mt-2">
                      Registrar primero
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
